import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { toast } from "sonner";
import { workLocationKeys } from "@/lib/query";
import type { WorkLocationCreateInput, WorkLocationUpdateInput } from "@/schemas/work-locations";

export interface WorkLocation {
  id: string;
  /** Code-stable token stored on `person.location`. Never editable. */
  key: string;
  /** Admin-editable display label. */
  name: string;
  sortOrder: number;
  isActive: boolean;
  /** How many people hold this location — one in use can't be deleted. */
  personCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * Every location, active and inactive, in admin order. Inactive ones are
 * included so a person who still holds a retired location renders with
 * its real label rather than a raw token — pickers filter with
 * `useWorkLocationOptions`.
 */
export function useWorkLocations(enabled = true) {
  return useQuery({
    queryKey: workLocationKeys.all,
    queryFn: async (): Promise<WorkLocation[]> => {
      const res = await fetch("/api/work-locations");
      if (!res.ok) throw new Error("Failed to fetch work locations");
      const json = await res.json();
      return json.data;
    },
    enabled,
  });
}

/**
 * Picker options (active only) plus a token → label map that covers retired
 * ones too. Every work-location `<Select>`, filter and badge reads from
 * here, so there is no second copy of the list anywhere in the client.
 */
export function useWorkLocationOptions() {
  const query = useWorkLocations();
  const rows = useMemo(() => query.data ?? [], [query.data]);
  return useMemo(
    () => ({
      isLoading: query.isLoading,
      options: rows.filter((r) => r.isActive).map((r) => ({ value: r.key, label: r.name })),
      labelFor: (key: string) => rows.find((r) => r.key === key)?.name ?? key,
    }),
    [rows, query.isLoading],
  );
}

async function mutate(url: string, method: string, body?: unknown) {
  const res = await fetch(url, {
    method,
    ...(body !== undefined && {
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }),
  });
  if (!res.ok) {
    // The delete and create refusals carry a specific message (one in use, a
    // built-in, a duplicate key); surfacing it is the whole point.
    const json = await res.json().catch(() => null);
    throw new Error(json?.error ?? "Request failed");
  }
  return res.status === 204 ? null : res.json().catch(() => null);
}

export function useCreateWorkLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkLocationCreateInput) => mutate("/api/work-locations", "POST", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workLocationKeys.all });
      toast.success("Work location created");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useUpdateWorkLocation(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: WorkLocationUpdateInput) =>
      mutate(`/api/work-locations/${id}`, "PUT", input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workLocationKeys.all });
      toast.success("Work location updated");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useReorderWorkLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, direction }: { id: string; direction: "up" | "down" }) =>
      mutate(`/api/work-locations/${id}/reorder`, "PATCH", { direction }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workLocationKeys.all });
    },
    onError: (e: Error) => toast.error(e.message),
  });
}

export function useDeleteWorkLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => mutate(`/api/work-locations/${id}`, "DELETE"),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workLocationKeys.all });
      toast.success("Work location deleted");
    },
    onError: (e: Error) => toast.error(e.message),
  });
}
